import { generateCharge, getAmount } from '@fv/client-core'
import type { Quote, SelectFieldOption } from '@fv/client-types'
import { fvDateUtc } from '@fv/dates'
import type { OpportunityStatus } from '@fv/models'

import { useAppSettings } from '@/hooks/settings'
import { useTypeNames } from '@/hooks/useTypeNames'
import type { Opportunity } from '@/types/Opportunity'

import { useQuotesFormState } from './QuotesFormProvider'
import type { Model } from './types'

export function quoteTerm(status: OpportunityStatus) {
  return status === 'confirmed' ? 'quote' : 'bid'
}

function deliveryDatesEqual(a?: Date | string, b?: Date | string) {
  if (!a && !b) return true
  if (!a || !b) return false
  return fvDateUtc(a).isSame(fvDateUtc(b), 'day')
}

function chargesChanged(model: Model, original: Quote) {
  return (
    model.charges?.some(
      charge =>
        !original.charges?.some(
          og => og.name === charge.name && og.amount === charge.amount,
        ),
    ) ||
    original.charges?.some(
      charge =>
        !model.charges?.some(
          d => d.amount === charge.amount && d.name === charge.name,
        ),
    )
  )
}

export function hasQuoteChanged(model: Model, original?: Quote) {
  if (!original) return false

  return (
    getAmount(model) !== getAmount(original) ||
    chargesChanged(model, original) ||
    model.equipmentType !== original.equipmentType ||
    model.quoteNum !== original.quoteNum ||
    !deliveryDatesEqual(model.deliveryDate, original.deliveryDate)
  )
}

export const createQuoteEditModel = (quote: Quote): Model => ({
  amount: quote.amount,
  currency: quote.currency,
  deliveryDate: quote.deliveryDate ? new Date(quote.deliveryDate) : undefined,
  equipmentType: quote.equipmentType,
  quoteNum: quote.quoteNum || '',
  quoteId: quote._id,
  charges: quote.charges?.map(c => generateCharge(c)),
})

export const useEquimentTypeOptions = (
  opp: Opportunity,
): SelectFieldOption[] => {
  const { equipmentName } = useTypeNames()
  const { type, alternateTypes } = opp.equipmentRequested
  return [
    {
      value: type,
      text: equipmentName(type),
    },
    ...(alternateTypes?.map(alt => ({
      text: equipmentName(alt),
      value: alt,
    })) ?? []),
  ]
}

export const useCanEditQuotes = (opp: Opportunity, quotes: Quote[]) => {
  const { data: settings } = useAppSettings()
  const { contractedRatesLocked } = useQuotesFormState()
  const hasApiRate = quotes.some(q => q.method === 'api')
  const hasContractedRate = quotes.some(q => q.method === 'upload')
  const hasManualRate = quotes.some(q => q.method === 'manual')

  return (
    hasManualRate ||
    (hasApiRate && settings.allowAPIQuoteEdit) ||
    (hasContractedRate && !contractedRatesLocked)
  )
}
